/**
 * The fleet's notices, drawn in the brand — what a page shows when a connector says "that did not
 * work" (or "done").
 *
 * The store is ra's `@martincastroalvarez/notices`: the same one isis' `IsisToastProvider` renders in
 * a console, which is why the books, courses and webinars connectors can serve both a console and the
 * learning centre without knowing which. `<Anubis>` mounts this once; an app never does.
 *
 * Drawn like isis' toast (owner, 2026-10-20: *"more similar to isis toasts"*): a pill on the surface
 * at the top — full width on a phone, a column at the top-right from 640px — with a rail down its
 * left edge and a glyph that carry the kind (the primary colour for done, danger for an error, the
 * second accent for a warning, muted for information), the words in the ink whatever the kind, and
 * a quiet ✕. It slides in and fades out. Each dismisses itself after its own duration (`0` stays until
 * closed), pausing while the pointer or the focus is on it; a repeated message refills its time. An
 * error is `role="alert"` so a screen reader hears it without going looking.
 */
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';

import { dismiss, getToasts, subscribe } from '@martincastroalvarez/notices';
import type { Toast } from '@martincastroalvarez/notices';

import { cx, mod } from './cx';
import { Icon } from './media';

/** How long the exit runs before the notice leaves the store — `a-notice-out` in anubis.css. */
const EXIT_MS = 180;

function Notice({ notice }: { notice: Toast }) {
  const [leaving, setLeaving] = useState(false);
  const [paused, setPaused] = useState(false);
  const remaining = useRef(notice.duration);
  const close = useCallback(() => {
    setLeaving(true);
    window.setTimeout(() => dismiss(notice.id), EXIT_MS);
  }, [notice.id]);
  // A repeated message is republished as a new object with the same id: it refills the time. Before
  // the countdown, so the countdown's cleanup debits first and this refills after (as isis does).
  useEffect(() => {
    remaining.current = notice.duration;
  }, [notice]);
  useEffect(() => {
    if (notice.duration === 0 || leaving || paused) return undefined;
    const started = Date.now();
    const timer = window.setTimeout(close, remaining.current);
    return () => {
      window.clearTimeout(timer);
      remaining.current = Math.max(0, remaining.current - (Date.now() - started));
    };
  }, [notice, leaving, paused, close]);
  const glyph =
    notice.variant === 'success' ? 'check' : notice.variant === 'info' ? null : 'warning';
  return (
    <div
      role={notice.variant === 'error' ? 'alert' : 'status'}
      className={cx(
        'a-notice-item',
        mod('a-notice-item', 'variant', notice.variant),
        leaving && 'a-notice-item--leaving',
      )}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      {glyph ? (
        <span className="a-notice-item__glyph">
          <Icon name={glyph} size="sm" />
        </span>
      ) : null}
      <span className="a-notice-item__message">{notice.message}</span>
      <button type="button" className="a-notice-item__close" aria-label="Dismiss" onClick={close}>
        <Icon name="close" size="sm" />
      </button>
    </div>
  );
}

/** Every live notice, stacked. Mounted by `<Anubis>`. */
export function Notices() {
  const notices = useSyncExternalStore(subscribe, getToasts, getToasts);
  if (notices.length === 0) return null;
  return (
    <div className="a-notices">
      {notices.map((notice) => (
        <Notice key={notice.id} notice={notice} />
      ))}
    </div>
  );
}
